import React from "react";
import {
  Box,
  Paper,
  Typography,
  LinearProgress,
  CircularProgress,
} from "@mui/material";
import {
  ChatBubbleOutline,
  PersonOutline,
  AutoAwesome,
  ThumbUpOutlined,
  CheckCircleOutline,
  SettingsOutlined,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";

// Custom styled LinearProgress for the gradient effect
const GradientLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 6,
  borderRadius: 6,
  backgroundColor: "#1e293b", // slate-800 equivalent
  "& .MuiLinearProgress-bar": {
    borderRadius: 6,
    backgroundImage:
      "linear-gradient(90deg, #22d3ee 0%, #3b82f6 50%, #9333ea 100%)", // Cyan -> Blue -> Purple
  },
}));

// Helper to assign icons dynamically based on keywords in the API step label
const getStepIcon = (label) => {
  const lowerLabel = label.toLowerCase();
  if (lowerLabel.includes("analyz") || lowerLabel.includes("transcript")) return ChatBubbleOutline;
  if (lowerLabel.includes("speaker")) return PersonOutline;
  if (lowerLabel.includes("insight")) return AutoAwesome;
  if (lowerLabel.includes("feedback")) return ThumbUpOutlined;
  return SettingsOutlined; // Default fallback icon
};

const StackedProgress = ({ apiProgress }) => {
// 1. Define fallback steps for when the API hasn't responded yet
  const defaultSteps = [
    "Creating Job...",
    "Analyzing Transcript...",
    "Preparing Personalised Feedback..."
  ];

  // 2. Use the API steps if they exist; otherwise, use the fallbacks
  const stepsList = apiProgress?.job_status_steps?.length > 0 
    ? apiProgress.job_status_steps 
    : defaultSteps;

  // 3. Fallback to the first step's label if the API status is missing
  const currentStatus = apiProgress?.job_status || stepsList[0];

  // 4. Derive the current step index dynamically
  let currentStep = stepsList.indexOf(currentStatus);
  
  // If the status isn't in the array (e.g., "Completed"), assume we are at the end.
  if (currentStep === -1) {
    currentStep = currentStatus.toLowerCase() === "completed" ? stepsList.length : 0;
  }

  // 5. Calculate Progress
  const totalSteps = Math.max(stepsList.length, 1);
  const progressTarget = ((currentStep + 1) / totalSteps) * 100;
  
  // Clamp progress between 5% (visual minimum) and 100%
  const progress = Math.min(100, Math.max(5, progressTarget));

  // Helper for dynamic styles
  const getStepStyles = (index) => {
    if (index === currentStep) {
      // ACTIVE (Cyan)
      return {
        bgcolor: "#22d3ee", // Cyan-400
        color: "#0f172a", // Slate-900
        transform: "scale(1.03)",
        boxShadow: "0 0 20px rgba(34, 211, 238, 0.4)",
        borderColor: "#22d3ee",
      };
    } else if (index < currentStep) {
      // COMPLETED (Dark Blue/Teal)
      return {
        bgcolor: "rgba(30, 41, 59, 0.5)", // Slate-800 with opacity
        color: "#cbd5e1", // Slate-300
        borderColor: "#334155", // Slate-700
      };
    } else {
      // PENDING (White/Grey)
      return {
        bgcolor: "#e2e8f0", // Slate-200
        color: "#64748b", // Slate-500
        borderColor: "#e2e8f0",
      };
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "rgba(0,0,0,.6)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "fixed",
        width: "100%",
        height: "100%",
        p: 2,
        left: 0,
        top: 0,
        zIndex: 9999,
      }}
    >
      <Paper
        elevation={10}
        sx={{
          width: "100%",
          maxWidth: 450,
          bgcolor: "#1a1b4b",
          borderRadius: 4,
          p: 8,
          position: "relative",
          overflow: "hidden",
          border: "1px solid rgba(255,255,255,0.1)",
        }}
      >
        {/* Header */}
        <Box textAlign="center" mb={4} mt={1}>
          <Typography
            variant="h5"
            component="h2"
            fontWeight="700"
            color="white"
            gutterBottom
          >
            Understanding Your Conversation
          </Typography>
          <Typography variant="body2" color="#94a3b8" sx={{ lineHeight: 1.6 }}>
            Preparing your personalised feedback, insights, and guidance for
            growth.
          </Typography>
        </Box>

        {/* Dynamic Steps List */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {stepsList.map((stepLabel, index) => {
            const Icon = getStepIcon(stepLabel);
            const isActive = index === currentStep;
            const isCompleted = index < currentStep;
            const styles = getStepStyles(index);

            return (
              <Paper
                key={index}
                variant="outlined"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  p: 2,
                  borderRadius: 3,
                  transition: "all 0.5s ease",
                  borderWidth: "2px",
                  ...styles,
                }}
              >
                {/* Icon Circle */}
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mr: 2,
                    flexShrink: 0,
                    transition: "background-color 0.3s",
                    bgcolor: isActive
                      ? "#0f172a"
                      : isCompleted
                        ? "#334155"
                        : "#cbd5e1",
                    color: isActive
                      ? "#22d3ee"
                      : isCompleted
                        ? "#94a3b8"
                        : "#64748b",
                  }}
                >
                  {isActive ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : isCompleted ? (
                    <CheckCircleOutline fontSize="small" />
                  ) : (
                    <Icon fontSize="small" />
                  )}
                </Box>

                <Typography
                  variant="body1"
                  fontWeight={isActive ? 600 : 500}
                  sx={{ fontSize: "0.95rem" }}
                >
                  {stepLabel}
                </Typography>
              </Paper>
            );
          })}
        </Box>

        {/* Custom Progress Bar */}
        <Box mt={5} mb={1}>
          <GradientLinearProgress variant="determinate" value={progress} />
        </Box>
      </Paper>
    </Box>
  );
};

export default StackedProgress;